/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { AppliedFilterDefinition } from "./types";
import {
  coerceToStringArray,
  getCountBasedDisplayValue,
  removeSelectedValue,
} from "./utils";

type CountFilterLabels = { singular: string; plural: string };

type CountFilterDefinitionArgs<
  TQuery extends QueryState,
  TKey extends string
> = {
  key: TKey;
  label: string;
  labels: CountFilterLabels;
  getValues: (queryState: TQuery) => string[] | undefined;
  getExcluded: (queryState: TQuery) => boolean | undefined;
  getValueLabel?: (value: string) => string;
  clear: (queryState: TQuery) => Partial<TQuery>;
  applyValuesPatch: (queryState: TQuery, values: string[]) => Partial<TQuery>;
};

export const createCountFilterDefinition = <
  TQuery extends QueryState,
  TKey extends string
>({
  key,
  label,
  labels,
  getValues,
  getExcluded,
  getValueLabel,
  clear,
  applyValuesPatch,
}: CountFilterDefinitionArgs<TQuery, TKey>): AppliedFilterDefinition<
  TQuery,
  TKey
> => ({
  key,
  label,
  getValues: (queryState) => coerceToStringArray(getValues(queryState)),
  ...(getValueLabel && {
    getValueLabel: (value) => getValueLabel(value),
  }),
  getValueDisplay: (queryState, values) => {
    if (values.length === 1 && getValueLabel) {
      const displayLabel = getValueLabel(values[0]);
      return getExcluded(queryState)
        ? `Exclude: ${displayLabel}`
        : displayLabel;
    }

    return getCountBasedDisplayValue(values, getExcluded(queryState), labels);
  },
  clear,
  removeValue: (queryState, valueToRemove) =>
    applyValuesPatch(
      queryState,
      removeSelectedValue(
        coerceToStringArray(getValues(queryState)),
        valueToRemove
      )
    ),
});
